import useTranslation from "@hooks/use_translation.ts";
import SectionDetailList from "@components/profile/section_detail_list.tsx";
import { optionLabel } from "@components/profile/profile_checklist_items.ts";
import type { ProfileData } from "@/queries/use_profile.ts";
import type { Option } from "@sprout/select";
import type { SelfIdentification } from "@utils/self_identification.ts";

type Props = { data: ProfileData };

const hispanicOrLatinoLabel = (
  race: SelfIdentification["race"],
  options: Option[] | undefined,
): string | undefined => {
  if (race == null) return undefined;

  const hispanicOptions = options ?? [];
  const direct = hispanicOptions.find((option) => String(option.value) === String(race));

  return direct ? direct.label : hispanicOptions.find((option) => option.value === "")?.label;
};

const selfIdValues = (data: ProfileData) => {
  const value = data.selfIdentification?.value;
  const options = data.selfIdentification?.options;

  return {
    gender: optionLabel(value?.gender, options?.genderOptions),
    hispanicOrLatino: hispanicOrLatinoLabel(value?.race, options?.hispanicOrLatinoOptions),
    race: optionLabel(value?.race, options?.raceOptions),
    veteranStatus: optionLabel(value?.veteranStatus, options?.veteranShortOptions),
    disabilityStatus: optionLabel(value?.disabilityStatus, options?.disabilityShortOptions),
  };
};

export const isVoluntarySelfIdEmpty = (data: ProfileData): boolean =>
  Object.values(selfIdValues(data)).every((value) => !value?.trim());

const VoluntarySelfIdBody = ({ data }: Props) => {
  const { t } = useTranslation("user", { keyPrefix: "profile_sections.voluntary_self_id" });
  const values = selfIdValues(data);

  return (
    <SectionDetailList
      rows={[
        { label: t("gender"), value: values.gender },
        { label: t("hispanic_or_latino"), value: values.hispanicOrLatino },
        { label: t("race"), value: values.race },
        { label: t("veteran_status"), value: values.veteranStatus },
        { label: t("disability_status"), value: values.disabilityStatus },
      ]}
    />
  );
};

export default VoluntarySelfIdBody;
